import React, { useState, useEffect } from 'react';
import {
    Form,
    Input,
    Button,
    Select,
    Row,
    Col,
    Modal,
    Card,
    Typography,
    Space,
    DatePicker,
    InputNumber,
    AutoComplete,
    Divider,
    Switch,
    message,
    App,
    Spin,
    Slider
} from 'antd';
import { EuroOutlined, PercentageOutlined, PlusOutlined } from '@ant-design/icons';
import { useAuth } from '../../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import { useStyles } from '../../theme/hooks';
import {
    getLoanTypes,
    getBanks,
    getPoolers,
    getReferredBy,
    updateLoan
} from '../../services/loanService';
import { getAllCustomers } from '../../services/customerService';
import { getAllConsultants } from '../../services/consultantService';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';

const { Option } = Select;

const useEditLoanFormStyles = () => useStyles((theme) => ({
    form: {
        width: '100%',
        maxWidth: '1024px',
        margin: '0 auto',
        padding: '0 16px',
        [`@media (maxWidth: ${theme.breakpoints.sm}px)`]: {
            padding: '0 8px',
        }
    },
    section: {
        marginBottom: theme.spacing.xl,
        padding: theme.spacing.lg,
        backgroundColor: theme.colors.background,
        borderRadius: theme.borderRadius.lg,
        boxShadow: theme.shadows.sm,
        [`@media (maxWidth: ${theme.breakpoints.sm}px)`]: {
            padding: theme.spacing.md,
            marginBottom: theme.spacing.lg,
        }
    },
    sectionTitle: {
        fontSize: theme.typography.fontSize.lg,
        fontWeight: theme.typography.fontWeight.bold,
        marginBottom: theme.spacing.md,
        color: theme.colors.text.primary
    },
    buttons: {
        marginTop: theme.spacing.xl,
        display: 'flex',
        justifyContent: 'flex-end',
        gap: theme.spacing.md,
        [`@media (maxWidth: ${theme.breakpoints.sm}px)`]: {
            justifyContent: 'stretch',
            '& button': {
                flex: 1
            }
        }
    }
}));

const EditLoanFormExtended = ({ initialValues, onSuccess }) => {
    const [form] = Form.useForm();
    const { t } = useTranslation();
    const { user } = useAuth();
    const [loading, setLoading] = useState(false);
    const [customers, setCustomers] = useState([]);
    const [consultants, setConsultants] = useState([]);
    const [loanTypes, setLoanTypes] = useState([]);
    const [banks, setBanks] = useState([]);
    const [filteredBanks, setFilteredBanks] = useState([]);
    const [poolers, setPoolers] = useState([]);
    const [referredBy, setReferredBy] = useState([]);
    const [isDirectSubmission, setIsDirectSubmission] = useState(false);
    const [isPrognosis, setIsPrognosis] = useState(false);
    const [hasSecondaryConsultant, setHasSecondaryConsultant] = useState(false);
    const [bankSearchText, setBankSearchText] = useState('');
    const navigate = useNavigate();
    const styles = useEditLoanFormStyles();

    useEffect(() => {
        const fetchData = async () => {
            try {
                const [
                    customersData,
                    consultantsData,
                    loanTypesData,
                    banksData,
                    poolersData,
                    referredByData
                ] = await Promise.all([
                    getAllCustomers(),
                    getAllConsultants(),
                    getLoanTypes(),
                    getBanks(),
                    getPoolers(),
                    getReferredBy()
                ]);

                setCustomers(customersData);
                setConsultants(consultantsData);
                setLoanTypes(loanTypesData);
                setBanks(banksData);
                setFilteredBanks(banksData);
                setPoolers(poolersData);
                setReferredBy(referredByData);

                // Set initial hasSecondaryConsultant state based on initialValues
                if (initialValues?.secondary_consultant_id) {
                    setHasSecondaryConsultant(true);
                }
            } catch (error) {
                message.error('Error loading form data');
                console.error('Error:', error);
            }
        };

        fetchData();
    }, [initialValues]);

    useEffect(() => {
        loadFormData();
        if (initialValues) {
            const formValues = {
                ...initialValues,
                customer: initialValues.customer?.id || initialValues.customer,
                loanType: initialValues.loan_type?.id || initialValues.loan_type,
                bank: initialValues.bank?.id || initialValues.bank,
                pooler: initialValues.pooler?.id || initialValues.pooler,
                referredBy: initialValues.referred_by?.id || initialValues.referred_by,
                reportedAt: initialValues.reported_at ? dayjs(initialValues.reported_at) : null,
                signatureDate: initialValues.signature_date ? dayjs(initialValues.signature_date) : null,
                amount: initialValues.amount,
                provision: initialValues.provision,
                interestLockInYears: initialValues.interest_lock_in_years,
                processNumber: initialValues.process_number,
                isDirectSubmission: initialValues.is_direct_submission,
                isPrognosis: initialValues.is_prognosis,
                primary_consultant_id: initialValues.primary_consultant?.id || initialValues.primary_consultant_id,
                secondary_consultant_id: initialValues.secondary_consultant?.id || initialValues.secondary_consultant_id,
                temporary_consultant_percentage: initialValues.temporary_consultant_percentage || 0,
                // Add date fields for status dependent fields
                statementReceivedDate: initialValues.statement_received_date ? dayjs(initialValues.statement_received_date) : null,
                statementReceivedNumber: initialValues.statement_received_number,
                invoiceDate: initialValues.invoice_date ? dayjs(initialValues.invoice_date) : null,
                invoiceNumber: initialValues.invoice_number,
                invoiceComment: initialValues.invoice_comment,
                consultantBilledDate: initialValues.consultant_billed_date ? dayjs(initialValues.consultant_billed_date) : null,
                consultantBilledNumber: initialValues.consultant_billed_number,
                temporaryBilledDate: initialValues.temporary_billed_date ? dayjs(initialValues.temporary_billed_date) : null,
                temporaryBilledNumber: initialValues.temporary_billed_number,
                paymentDate: initialValues.payment_date ? dayjs(initialValues.payment_date) : null,
            };
            
            form.setFieldsValue(formValues);
            setIsDirectSubmission(formValues.isDirectSubmission);
            setIsPrognosis(formValues.isPrognosis);

            console.log('Setting form values:', formValues);
        }
    }, [initialValues, form]);

    useEffect(() => {
        if (initialValues) {
            const hasPooler = !!initialValues.pooler;
            setIsDirectSubmission(!hasPooler);
            form.setFieldValue('isDirectSubmission', !hasPooler);
        }
    }, [initialValues, form]);

    const loadFormData = async () => {
        try {
            setLoading(true);
            const [customersData, loanTypesData, banksData, poolersData, referredByData] = await Promise.all([
                getAllCustomers(),
                getLoanTypes(),
                getBanks(),
                getPoolers(),
                getReferredBy()
            ]);

            setCustomers(customersData);
            setLoanTypes(loanTypesData);
            setBanks(banksData);
            setFilteredBanks(banksData);
            setPoolers(poolersData);
            setReferredBy(referredByData);
        } catch (error) {
            console.error('Error loading form data:', error);
            message.error(t('loans.errors.loadingFailed'));
        } finally {
            setLoading(false);
        }
    };

    const onFinish = async (values) => {
        setLoading(true);
        try {
            const formData = {
                customer_id: values.customer,
                loan_type_id: values.loanType,
                bank_id: values.bank,
                pooler_id: values.pooler,
                referred_by_id: values.referredBy,
                amount: values.amount,
                provision: values.provision,
                interest_lock_in_years: values.interestLockInYears,
                process_number: values.processNumber,
                reported_at: values.reportedAt?.format('YYYY-MM-DD'),
                signature_date: values.signatureDate?.format('YYYY-MM-DD'),
                is_direct_submission: isDirectSubmission,
                is_prognosis: isPrognosis,
                primary_consultant_id: values.primary_consultant_id,
                secondary_consultant_id: values.secondary_consultant_id,
                temporary_consultant_percentage: values.temporary_consultant_percentage,
                invoice_date: values.invoiceDate?.format('YYYY-MM-DD'),
                invoice_number: values.invoiceNumber,
                payment_date: values.paymentDate?.format('YYYY-MM-DD'),
                statement_received_date: values.statementReceivedDate?.format('YYYY-MM-DD'),
                statement_received_number: values.statementReceivedNumber,
                consultant_billed_date: values.consultantBilledDate?.format('YYYY-MM-DD'),
                consultant_billed_number: values.consultantBilledNumber,
                temporary_billed_date: values.temporaryBilledDate?.format('YYYY-MM-DD'),
                temporary_billed_number: values.temporaryBilledNumber,
                invoice_comment: values.invoiceComment
            };

            await updateLoan(initialValues.id, formData);
            if (onSuccess) {
                onSuccess();
            }
            navigate(`/loans/${initialValues.id}?ts=${Date.now()}`); // Add timestamp to force reload
        } catch (error) {
            message.error(error.message || t('loans.errors.updateFailed'));
        } finally {
            setLoading(false);
        }
    };

    const onBankSearch = (value) => {
        setBankSearchText(value);
        const filtered = banks.filter(bank => 
            bank.name.toLowerCase().includes(value.toLowerCase())
        );
        setFilteredBanks(filtered);
    };

    const renderStatusDependentFields = () => {
        if (!initialValues?.status) return null;

        const status = initialValues.status;
        
        return (
          <>
            <div style={styles.section}>
              <h3 style={styles.sectionTitle}>{t('loan.details.payment')}</h3>
              <Row gutter={24}>
                {/* Fields for PAYMENT_CONFIRMED and higher */}
                {['PAYMENT_CONFIRMED', 'PAID', 'CONSULTANT_BILLED'].includes(status) && (
                  <>
                    <Col span={12}>
                      <Form.Item
                        name="statementReceivedDate"
                        label={t('loan.fields.statementReceivedDate')}
                      >
                        <DatePicker style={{ width: '100%' }} />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item
                        name="statementReceivedNumber"
                        label={t('loan.fields.statementReceivedNumber')}
                      >
                        <Input />
                      </Form.Item>
                    </Col>
                  </>
                )}

                {/* Fields for PAID and higher */}
                {['PAID', 'CONSULTANT_BILLED'].includes(status) && (
                  <>
                    <Col span={12}>
                      <Form.Item
                        name="invoiceDate"
                        label={t('loan.fields.invoiceDate')}
                      >
                        <DatePicker style={{ width: '100%' }} />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item
                        name="invoiceNumber"
                        label={t('loan.fields.invoiceNumber')}
                      >
                        <Input />
                      </Form.Item>
                    </Col>
                    <Col span={24}>
                      <Form.Item
                        name="invoiceComment"
                        label={t('loan.fields.invoiceComment')}
                      >
                        <Input.TextArea rows={2} />
                      </Form.Item>
                    </Col>
                  </>
                )}

                {/* Fields for CONSULTANT_BILLED */}
                {status === 'CONSULTANT_BILLED' && (
                  <>
                    <Col span={12}>
                      <Form.Item
                        name="consultantBilledDate"
                        label={t('loan.fields.consultantBilledDate')}
                      >
                        <DatePicker style={{ width: '100%' }} />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item
                        name="consultantBilledNumber"
                        label={t('loan.fields.consultantBilledNumber')}
                      >
                        <Input />
                      </Form.Item>
                    </Col>
                    {initialValues.temporary_consultant && (
                      <>
                        <Col span={12}>
                          <Form.Item
                            name="temporaryBilledDate"
                            label={t('loan.fields.temporaryBilledDate')}
                          >
                            <DatePicker style={{ width: '100%' }} />
                          </Form.Item>
                        </Col>
                        <Col span={12}>
                          <Form.Item
                            name="temporaryBilledNumber"
                            label={t('loan.fields.temporaryBilledNumber')}
                          >
                            <Input />
                          </Form.Item>
                        </Col>
                      </>
                    )}
                  </>
                )}
              </Row>
            </div>
          </>
        );
    };

    if (loading && !loanTypes.length) {
        return <Spin size="large" />;
    }

    return (
        <div style={styles.form}>
            <Form
                form={form}
                layout="vertical"
                onFinish={onFinish}
                style={styles.form}
            >
                {/* Process Information */}
                <div style={styles.section}>
                <h3 style={styles.sectionTitle}>{t('loan.fields.customer')}</h3>
                <Row gutter={[16, 16]}>
                        <Col xs={24} sm={24} md={12}>
                            <Form.Item
                                name="customer"
                                label={t('loan.fields.customer')}
                                rules={[{ required: true, message: t('validation.required') }]}
                            >
                                <Select>
                                    {customers.map(customer => (
                                        <Option key={customer.id} value={customer.id}>
                                            {`${customer.name || ''} ${customer.first_name || ''}`}
                                        </Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>
                        <Col xs={24} sm={24} md={12}>
                            <Form.Item
                                name="processNumber"
                                label={t('loan.fields.processNumber')}
                                rules={[{ required: true, message: t('validation.required') }]}
                            >
                                <Input />
                            </Form.Item>
                        </Col>
                    </Row>
                </div>


                {/* Bank Information */}
                <div style={styles.section}>
                <h3 style={styles.sectionTitle}>{t('loan.fields.bank')}</h3>
                <Row gutter={[16, 16]}>
                        <Col xs={24} sm={24} md={12}>
                            <Form.Item
                                name="bank"
                                label={t('loan.fields.bank')}
                                rules={[{ required: true, message: t('common.validation.required') }]}
                            >
                                <Select
                                    showSearch
                                    placeholder={t('loan.fields.bankSelect')}
                                    optionFilterProp="children"
                                    onSearch={onBankSearch}
                                    value={initialValues?.bank?.id}
                                >
                                    {filteredBanks.map(bank => (
                                        <Option key={bank.id} value={bank.id}>
                                            {bank.name}
                                        </Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>
                        <Col xs={24} sm={24} md={12}>
                            <Form.Item
                                name="referredBy"
                                label={t('loan.fields.referredBy')}
                            >
                                <Select>
                                    {referredBy.map(referrer => (
                                        <Option key={referrer.id} value={referrer.id}>{referrer.name + ',' + referrer.first_name}</Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={[16, 16]}>
                        <Col xs={24} sm={12} md={6}>
                            <Form.Item
                                name="isDirectSubmission"
                                label=""
                                valuePropName="checked"
                            >
                                <Switch 
                                    checkedChildren={t('loan.fields.directSubmission')}
                                    unCheckedChildren={t('loan.fields.directSubmission')}
                                    onChange={(checked) => {
                                        setIsDirectSubmission(checked);
                                        if (checked) {
                                            form.setFieldValue('pooler', undefined);
                                        }
                                    }}
                                />
                            </Form.Item>
                        </Col>
                        <Col xs={24} sm={12} md={6}>
                            <Form.Item
                                name="pooler"
                                label={t('loan.fields.pooler')}
                                hidden={isDirectSubmission}
                            >
                                <Select 
                                    allowClear
                                    onChange={(value) => {
                                        const newDirectSubmission = !value;
                                        setIsDirectSubmission(newDirectSubmission);
                                        form.setFieldValue('isDirectSubmission', newDirectSubmission);
                                    }}
                                >
                                    {poolers.map(pooler => (
                                        <Option key={pooler.id} value={pooler.id}>
                                            {pooler.name}
                                        </Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>
                        <Col xs={24} sm={12} md={6}>
                            <Form.Item
                                name="signatureDate"
                                label={t('loan.fields.signatureDate')}
                            >
                                <DatePicker style={{ width: '100%' }} />
                            </Form.Item>
                        </Col>
                    </Row>
                </div>


                {/* Loan Details */}
                <div style={styles.section}>
                <h3 style={styles.sectionTitle}>{t('loan.fields.loan')}</h3>
                <Row gutter={[16, 16]}>
                        <Col xs={24} sm={12} md={6}>
                            <Form.Item
                                name="amount"
                                label={t('loan.fields.amount')}
                                rules={[{ required: true, message: t('validation.required') }]}
                            >
                                <InputNumber
                                    style={{ width: '100%' }}
                                    formatter={value => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, '.')}
                                    parser={value => value.replace(/\./g, '')}
                                    prefix={<EuroOutlined />}
                                />
                            </Form.Item>
                        </Col>
                        <Col xs={24} sm={12} md={6}>
                            <Form.Item
                                name="loanType"
                                label={t('loan.fields.loanType')}
                                rules={[{ required: true, message: t('validation.required') }]}
                            >
                                <Select>
                                    {loanTypes.map(type => (
                                        <Option key={type.id} value={type.id}>{type.name}</Option>
                                    ))}
                                </Select>
                            </Form.Item>                        
                        </Col>
                        <Col xs={24} sm={12} md={6}>
                            <Form.Item
                                name="interestLockInYears"
                                label={t('loan.fields.interestLock')}
                                rules={[{ required: true, message: t('validation.required') }]}
                            >
                                <InputNumber style={{ width: '100%' }} min={1} max={30} />
                            </Form.Item>
                        </Col>
                        <Col xs={24} sm={12} md={6}>
                            <Form.Item
                                name="provision"
                                label={t('loan.fields.provision')}
                                rules={[{ required: true, message: t('validation.required') }]}
                            >
                                <InputNumber
                                    style={{ width: '100%' }}
                                    min={0}
                                    max={100}
                                    precision={10}
                                    step={0.01}
                                    prefix={<PercentageOutlined />}
                                />
                            </Form.Item>
                        </Col>
                        <Col xs={24} sm={12} md={6}>
                            <Form.Item
                                name="extra_bonus"
                                label={t('loan.fields.extraBonus')}
                                rules={[{ required: false, message: t('common.validation.required') }]}
                            >
                                <InputNumber
                                    style={{ width: '100%' }}
                                    formatter={value => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, '.')}
                                    parser={value => value.replace(/\./g, '')}
                                />
                            </Form.Item>
                        </Col>
                    </Row>
                </div>
                {/* Consultant Information */}
                <div style={styles.section}>
                <h3 style={styles.sectionTitle}>{t('loan.fields.consultants')}</h3>
                <Row gutter={[16, 16]}>
                        <Col xs={24} sm={24} md={8}>
                            <Form.Item
                                name="primary_consultant_id"
                                label={t('loan.fields.primaryConsultant')}
                                rules={[{ required: true, message: t('validation.required') }]}
                            >
                                <Select
                                    showSearch
                                    placeholder={t('loanForm.placeholders.selectConsultant')}
                                    optionFilterProp="children"
                                    filterOption={(input, option) =>
                                        option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                                    }
                                >
                                    {consultants.map(consultant => (
                                        <Option key={consultant.id} value={consultant.id}>
                                            {consultant.first_name} {consultant.last_name}
                                        </Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>

                        <Col xs={24} sm={24} md={8}>
                            <Form.Item
                                name="secondary_consultant_id"
                                label={t('loan.fields.temporaryConsultant')}
                            >
                                <Select
                                    showSearch
                                    placeholder={t('common.search')}
                                    optionFilterProp="children"
                                    filterOption={(input, option) =>
                                        option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                                    }
                                    onChange={(value) => setHasSecondaryConsultant(!!value)}
                                    allowClear
                                >
                                    {consultants.map(consultant => (
                                        <Option key={consultant.id} value={consultant.id}>
                                            {consultant.first_name} {consultant.last_name}
                                        </Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>

                        <Col xs={24} sm={24} md={8}>
                            <Form.Item
                                name="temporary_consultant_percentage"
                                label={t('loan.fields.temporary_share')}
                            >
                                <Slider
                                    min={0}
                                    max={100}
                                    disabled={!hasSecondaryConsultant}
                                    value={form.getFieldValue('temporary_consultant_percentage')}
                                    onChange={value => form.setFieldValue('temporary_consultant_percentage', value)}
                                    tooltip={{
                                        formatter: value => `${value}%`
                                    }}
                                    marks={{
                                        0: '0%',
                                        25: '25%',
                                        50: '50%',
                                        75: '75%',
                                        100: '100%'
                                    }}
                                />
                            </Form.Item>
                        </Col>
                    </Row>
                </div>
                {/* Payment Information */}
                <div style={styles.section}>
                    <h3 style={styles.sectionTitle}>{t('loan.details.payment')}</h3>
                    <Row gutter={[16, 16]}>
                        <Col xs={24} sm={12} md={8}>
                            <Form.Item
                                name="invoiceDate"
                                label={t('common.invoiceDate')}
                            >
                                <DatePicker style={{ width: '100%' }} />
                            </Form.Item>
                        </Col>
                        <Col xs={24} sm={12} md={8}>
                            <Form.Item
                                name="invoiceNumber"
                                label={t('common.invoiceNumber')}
                            >
                                <Input />
                            </Form.Item>
                        </Col>
                        <Col xs={24} sm={12} md={8}>
                            <Form.Item
                                name="paymentDate"
                                label={t('loan.fields.paymentDate')}
                            >
                                <DatePicker style={{ width: '100%' }} />
                            </Form.Item>
                        </Col>
                        <Col xs={24} sm={12} md={8}>
                            <Form.Item
                                name="statementReceivedDate"
                                label={t('common.statementReceivedDate')}
                            >
                                <DatePicker style={{ width: '100%' }} />
                            </Form.Item>
                        </Col>
                        <Col xs={24} sm={12} md={8}>
                            <Form.Item
                                name="statementReceivedNumber"
                                label={t('common.statementReceivedNumber')}
                            >
                                <Input />
                            </Form.Item>
                        </Col>
                    </Row>
                </div>



                {/* Consultant Information */}
                <div style={styles.section}>
                <h3 style={styles.sectionTitle}>{t('loan.fields.consultants')}</h3>
                    <Row gutter={[16, 16]}>
                        <Col xs={24} sm={12} md={8}>
                            <Form.Item
                                name="consultantBilledDate"
                                label={t('loan.billing.consultantBilledDate')}
                            >
                                <DatePicker style={{ width: '100%' }} />
                            </Form.Item>
                        </Col>
                        <Col xs={24} sm={12} md={8}>
                            <Form.Item
                                name="consultantBilledNumber"
                                label={t('loan.billing.consultantBilledNumber')}
                            >
                                <Input />
                            </Form.Item>
                        </Col>
                        </Row>
                        <Row gutter={[16, 16]}>                        
                        <Col xs={24} sm={12} md={8}>
                            <Form.Item
                                name="temporaryBilledDate"
                                label={t('loan.billing.temporaryBilledDate')}
                            >
                                <DatePicker style={{ width: '100%' }} />
                            </Form.Item>
                        </Col>
                        <Col xs={24} sm={12} md={8}>
                            <Form.Item
                                name="temporaryBilledNumber"
                                label={t('loan.billing.temporaryBilledNumber')}
                            >
                                <Input />
                            </Form.Item>
                        </Col>
                        <Col xs={24}>
                            <Form.Item
                                name="invoiceComment"
                                label={t('loan.billing.invoiceComment')}
                            >
                                <Input.TextArea rows={2} />
                            </Form.Item>
                        </Col>
                    </Row>
                </div>


                <Row>
                    <Col span={24}>
                        <div style={styles.buttons}>
                            <Button onClick={() => navigate(`/loans/${initialValues.id}`)}>
                                {t('common.cancel')}
                            </Button>
                            <Button type="primary" htmlType="submit" loading={loading}>
                                {t('common.save')}
                            </Button>
                        </div>
                    </Col>
                </Row>
            </Form>
        </div>
    );
};

export default EditLoanFormExtended;
